import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, useInfiniteQuery, InfiniteData } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import {
  sendDiscoveryMessageWithRecovery,
  getDiscoveryMessages,
  getDiscoveryContext,
  getDiscoveryInsights,
  getDiscoverySessions,
  getDiscoverySession,
  getDiscoveryPrompts,
  getSessionIdeas,
  updateMessageFeedback,
  discoveryKeys,
  DiscoverySessionMessage,
  SendDiscoveryMessageResponse,
  DiscoveryMessagesResponse,
  DiscoverySessionInsight,
  SessionDiscoveryIdea,
  OnePager,
  createOnePager,
  fetchSessionOnePagers,
  CreateOnePagerVariables,
} from '../api/discoveryAgent';
import { useIdeaFilters } from './useIdeaFilters';
import { buildSessionFilters, getCustomerParentKeysFromConfig } from '../components/explore/utils/buildSessionFilters';
import { useDiscoverySessionUrl } from './useDiscoverySessionUrl';
import { useDiscoveryStore } from '../stores/useDiscoveryStore';
import { useAbortOnUnmount } from './useAbortOnUnmount';

const MESSAGES_PAGE_SIZE = 20;
const INSIGHTS_PAGE_SIZE = 10;
const MESSAGES_STALE_MS = 30 * 1000;
const SESSION_IDEAS_STALE_MS = 30 * 1000;
const CONTEXT_LIVE_STALE_MS = 10 * 1000;
const SESSIONS_STALE_MS = 60 * 1000;
const PROMPTS_STALE_MS = 5 * 60 * 1000;

function patchDiscoverySessionLimitInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  sessionId: string,
  limitFields: Pick<
    SendDiscoveryMessageResponse,
    'isUserMessageLimitReached' | 'userMessageCount' | 'userMessageLimit'
  >
): void {
  if (!limitFields.isUserMessageLimitReached) return;

  queryClient.setQueryData(
    [...discoveryKeys.sessions(), 'byId', sessionId],
    (old: import('../api/discoveryAgent').DiscoverySession | null | undefined) => {
      if (!old) return old;
      return {
        ...old,
        isUserMessageLimitReached: true,
        userMessageCount: limitFields.userMessageCount ?? old.userMessageCount,
        userMessageLimit: limitFields.userMessageLimit ?? old.userMessageLimit,
      };
    }
  );

  queryClient.setQueryData(
    discoveryKeys.sessions(),
    (old: { sessions?: import('../api/discoveryAgent').DiscoverySession[] } | undefined) => {
      if (!old?.sessions) return old;
      return {
        ...old,
        sessions: old.sessions.map((session) =>
          session.id === sessionId || session.sessionId === sessionId
            ? {
              ...session,
              isUserMessageLimitReached: true,
              userMessageCount: limitFields.userMessageCount ?? session.userMessageCount,
              userMessageLimit: limitFields.userMessageLimit ?? session.userMessageLimit,
            }
            : session
        ),
      };
    }
  );
}

export { patchDiscoverySessionLimitInCache };

// ----- useDiscoveryChat -----

export function useDiscoveryChat(
  sessionId: string,
  options?: { onSuggestedIdeaReceived?: (artifact: { title: string; description: string }) => void }
) {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  const { ideasFiltersConfig } = useIdeaFilters();
  const customerParentKeys = getCustomerParentKeysFromConfig(ideasFiltersConfig ?? {});
  const runUntilUnmount = useAbortOnUnmount();

  const mutation = useMutation({
    mutationFn: async (message: string) => {
      const storeFilters = useDiscoveryStore.getState().sessionFilters;
      const uiFilters = buildSessionFilters(storeFilters, customerParentKeys);
      return runUntilUnmount((signal) =>
        sendDiscoveryMessageWithRecovery(sessionId, message, auth0, uiFilters, { signal })
      );
    },
    onMutate: async (message: string) => {
      await queryClient.cancelQueries({ queryKey: discoveryKeys.messages(sessionId) });
      await queryClient.cancelQueries({ queryKey: discoveryKeys.context(sessionId) });
      await queryClient.cancelQueries({ queryKey: discoveryKeys.insights(sessionId) });

      const optimisticUserMessage: DiscoverySessionMessage = {
        id: `temp-${Date.now()}`,
        sessionId,
        userId: auth0.user?.sub ?? '',
        messageType: 'user',
        content: message,
        timestamp: new Date().toISOString(),
      };

      queryClient.setQueryData(discoveryKeys.messages(sessionId), (old: unknown) => {
        const prev = old as { pages: DiscoveryMessagesResponse[]; pageParams: unknown[] } | undefined;
        if (!prev) {
          return {
            pages: [{ success: true, messages: [optimisticUserMessage], hasMore: false }],
            pageParams: [undefined],
          };
        }
        const newPages = [...prev.pages];
        if (newPages.length > 0) {
          newPages[0] = {
            ...newPages[0],
            messages: [optimisticUserMessage, ...(newPages[0].messages ?? [])],
          };
        } else {
          newPages.push({ success: true, messages: [optimisticUserMessage], hasMore: false });
        }
        return { ...prev, pages: newPages };
      });

      return { previousMessages: queryClient.getQueryData(discoveryKeys.messages(sessionId)) };
    },
    onError: (_err, _message) => {
      queryClient.invalidateQueries({ queryKey: discoveryKeys.messages(sessionId) });
    },
    onSuccess: async (data: SendDiscoveryMessageResponse) => {
      patchDiscoverySessionLimitInCache(queryClient, sessionId, data);
      if (data.isUserMessageLimitReached) {
        queryClient.invalidateQueries({ queryKey: [...discoveryKeys.sessions(), 'byId', sessionId] });
      }
      queryClient.invalidateQueries({ queryKey: discoveryKeys.context(sessionId) });
      queryClient.invalidateQueries({ queryKey: discoveryKeys.insights(sessionId) });
      queryClient.invalidateQueries({ queryKey: discoveryKeys.sessionIdeas(sessionId) });

      const suggestedArtifact = data.artifacts?.[0]?.type === 'suggested_idea' ? data.artifacts[0] : undefined;
      if (suggestedArtifact) {
        // Blank the agent's response text in the existing cache so it renders as nothing.
        // Use a functional update to preserve all existing infinite-query pages instead of
        // replacing them with a single freshly-fetched page (which would drop older pages).
        const responseText = data.response?.trim() ?? '';
        let blankedMatchingMessage = false;
        if (responseText) {
          queryClient.setQueryData(discoveryKeys.messages(sessionId), (old: any) => {
            if (!old?.pages) return old;
            // Only blank the single newest matching agent message (the reply we just
            // received). Multiple older bubbles may share identical wording; matching
            // purely on text would wipe those too.
            let target: any = null;
            for (const page of old.pages) {
              for (const m of page.messages ?? []) {
                if (m.messageType === 'agent' && m.content?.trim() === responseText) {
                  if (!target || new Date(m.timestamp).getTime() > new Date(target.timestamp).getTime()) {
                    target = m;
                  }
                }
              }
            }
            if (!target) return old;
            blankedMatchingMessage = true;
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                messages: (page.messages ?? []).map((m: any) =>
                  m === target ? { ...m, content: '' } : m
                ),
              })),
            };
          });
        }
        // If we couldn't blank a cached agent bubble (empty response, or no cached
        // message matched the reply yet), invalidate so the optimistic user message
        // from onMutate gets reconciled with the server instead of lingering.
        if (!blankedMatchingMessage) {
          queryClient.invalidateQueries({ queryKey: discoveryKeys.messages(sessionId) });
        }
        options?.onSuggestedIdeaReceived?.({
          title: suggestedArtifact.title ?? '',
          description: suggestedArtifact.description ?? '',
        });
      } else {
        queryClient.invalidateQueries({ queryKey: discoveryKeys.messages(sessionId) });
      }
    },
  });

  return {
    sendMessage: mutation.mutateAsync,
    isSending: mutation.isPending,
    error: mutation.error?.message ?? null,
    reset: mutation.reset,
  };
}

// ----- useDiscoveryMessages -----

const PROCESSING_POLL_INTERVAL_MS = 3000;

export function useDiscoveryMessages(
  sessionId: string,
  options: { enabled?: boolean; isBackendProcessing?: boolean } = {}
) {
  const auth0 = useAuth0();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: discoveryKeys.messages(sessionId),
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) =>
      getDiscoveryMessages(
        sessionId,
        { limit: MESSAGES_PAGE_SIZE, before: pageParam },
        auth0
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: DiscoveryMessagesResponse) => {
      if (!lastPage.hasMore || !lastPage.messages?.length) return undefined;
      const oldest = lastPage.messages[lastPage.messages.length - 1];
      return typeof oldest.timestamp === 'string' ? oldest.timestamp : (oldest as { timestamp: string }).timestamp;
    },
    enabled: !!sessionId && options.enabled !== false,
    staleTime: MESSAGES_STALE_MS,
    refetchOnWindowFocus: true,
    refetchInterval: options.isBackendProcessing ? PROCESSING_POLL_INTERVAL_MS : false,
  });

  const messages = (data?.pages ?? [])
    .flatMap((p) => p.messages ?? [])
    .sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

  return {
    messages,
    isLoading,
    isFetching,
    error: error?.message ?? null,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    refetch,
  };
}

type DiscoveryMessagesInfiniteData = InfiniteData<DiscoveryMessagesResponse>;

function applyMessageFeedbackToInfiniteData(
  data: DiscoveryMessagesInfiniteData | undefined,
  messageId: string,
  feedback: DiscoverySessionMessage['feedback'] | undefined
): DiscoveryMessagesInfiniteData | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      messages: (page.messages ?? []).map((m) => {
        const mid = m._id != null ? String(m._id) : m.id != null ? String(m.id) : '';
        if (mid !== messageId) return m;
        if (feedback == null) {
          const { feedback: _removed, ...rest } = m;
          return rest as DiscoverySessionMessage;
        }
        return { ...m, feedback };
      }),
    })),
  };
}

export type UpdateDiscoveryMessageFeedbackVariables = {
  messageId: string;
  rating: 'helpful' | 'not_helpful' | null;
};

/** Optimistic feedback on {@link discoveryKeys.messages}; rolls back on error. */
export function useMessageFeedback(sessionId: string) {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ messageId, rating }: UpdateDiscoveryMessageFeedbackVariables) => {
      if (!sessionId) {
        throw new Error('Session id is required to update message feedback');
      }
      await updateMessageFeedback(sessionId, messageId, rating, auth0);
    },
    onMutate: async ({ messageId, rating }) => {
      if (!sessionId) {
        return { previousData: undefined as DiscoveryMessagesInfiniteData | undefined };
      }
      await queryClient.cancelQueries({ queryKey: discoveryKeys.messages(sessionId) });
      const previousData = queryClient.getQueryData<DiscoveryMessagesInfiniteData>(
        discoveryKeys.messages(sessionId)
      );
      const optimisticFeedback: DiscoverySessionMessage['feedback'] | undefined =
        rating == null
          ? undefined
          : { rating, updatedAt: new Date().toISOString() };
      if (previousData) {
        const next = applyMessageFeedbackToInfiniteData(
          previousData,
          messageId,
          optimisticFeedback
        );
        if (next) {
          queryClient.setQueryData(discoveryKeys.messages(sessionId), next);
        }
      }
      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (!sessionId) return;
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(discoveryKeys.messages(sessionId), context.previousData);
      }
    },
    onSettled: () => {
      if (!sessionId) return;
      queryClient.invalidateQueries({ queryKey: discoveryKeys.messages(sessionId) });
    },
  });
}

// ----- useDiscoveryContext -----

export function useDiscoveryContext(sessionId: string) {
  const auth0 = useAuth0();
  const activeMessageTimestamp = useDiscoveryStore((s) => s.activeMessageTimestamp);
  const at = activeMessageTimestamp?.toISOString();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: discoveryKeys.context(sessionId, at),
    queryFn: async () => getDiscoveryContext(sessionId, at, auth0),
    enabled: !!sessionId,
    staleTime: at ? Infinity : CONTEXT_LIVE_STALE_MS,
  });

  return {
    context: data?.context ?? null,
    isLoading,
    error: error?.message ?? null,
    refetch,
  };
}

// ----- useDiscoveryInsights -----

export function useDiscoveryInsights(sessionId: string) {
  const auth0 = useAuth0();
  const activeMessageTimestamp = useDiscoveryStore((s) => s.activeMessageTimestamp);
  const before = activeMessageTimestamp?.toISOString();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: discoveryKeys.insights(sessionId, before),
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) =>
      getDiscoveryInsights(
        sessionId,
        { limit: INSIGHTS_PAGE_SIZE, before, cursor: pageParam },
        auth0
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: { insights: DiscoverySessionInsight[]; hasMore?: boolean }) => {
      if (!lastPage.hasMore || !lastPage.insights?.length) return undefined;
      const oldest = lastPage.insights[lastPage.insights.length - 1];
      return typeof oldest.timestamp === 'string' ? oldest.timestamp : (oldest as { timestamp: string }).timestamp;
    },
    enabled: !!sessionId,
    staleTime: before ? Infinity : CONTEXT_LIVE_STALE_MS,
  });

  const insights = (data?.pages ?? []).flatMap((p) => p.insights ?? []);

  return {
    insights,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    isLoading,
    error: error?.message ?? null,
    refetch,
  };
}

// ----- useSessionDiscoveryIdeas -----

export function useSessionDiscoveryIdeas(sessionId: string) {
  const auth0 = useAuth0();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: discoveryKeys.sessionIdeas(sessionId),
    queryFn: () => getSessionIdeas(sessionId, auth0),
    enabled: !!sessionId,
    staleTime: SESSION_IDEAS_STALE_MS,
    refetchOnWindowFocus: true,
  });

  const ideas: SessionDiscoveryIdea[] = data?.ideas ?? [];

  const artifactIdeaIdByAlias = useMemo(() => {
    const m = new Map<string, string>();
    for (const idea of ideas) {
      if (!idea._id) continue;
      const key = idea.artifactId ?? idea.artifactsId;
      if (key) m.set(key.trim(), idea._id);
    }
    return m;
  }, [ideas]);

  return {
    ideas,
    artifactIdeaIdByAlias,
    isLoading,
    error: error?.message ?? null,
    refetch,
  };
}

// ----- useDiscoverySessions -----

export function useDiscoverySessions(options: { enabled?: boolean } = {}) {
  const auth0 = useAuth0();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: discoveryKeys.sessions(),
    queryFn: () => getDiscoverySessions(auth0),
    enabled: options.enabled !== false,
    staleTime: SESSIONS_STALE_MS,
    refetchOnWindowFocus: true,
  });

  return {
    sessions: data?.sessions ?? [],
    isLoading,
    error: error?.message ?? null,
    refetch,
  };
}

// ----- useActiveDiscoverySession -----

const STALE_PROCESSING_THRESHOLD_MS = 15 * 60 * 1000;

function isSessionProcessing(session: { chatStatus?: string; chatStatusUpdatedAt?: string } | null): boolean {
  if (!session || session.chatStatus !== 'processing' || !session.chatStatusUpdatedAt) return false;
  const elapsedMs = Date.now() - new Date(session.chatStatusUpdatedAt).getTime();
  return elapsedMs < STALE_PROCESSING_THRESHOLD_MS;
}

export function useActiveDiscoverySession() {
  const { sessionId } = useDiscoverySessionUrl();
  const { sessions } = useDiscoverySessions({ enabled: true });
  const sessionFromList = useMemo(
    () => sessions.find((s) => s.id === sessionId || s.sessionId === sessionId) ?? null,
    [sessions, sessionId]
  );
  const auth0 = useAuth0();
  const { data: sessionById } = useQuery({
    queryKey: [...discoveryKeys.sessions(), 'byId', sessionId ?? ''],
    queryFn: () => getDiscoverySession(sessionId!, auth0),
    enabled: !!sessionId,
    staleTime: SESSIONS_STALE_MS,
    refetchInterval: isSessionProcessing(sessionFromList ?? null)
      ? PROCESSING_POLL_INTERVAL_MS
      : false,
  });
  const session = sessionById ?? sessionFromList ?? null;
  const isSessionFromDirectNavigation = Boolean(sessionId && session && !sessionFromList && sessionById);
  return { session, sessionId, isSessionFromDirectNavigation };
}

// ----- useDiscoveryPrompts -----

export function useDiscoveryPrompts(options: { enabled?: boolean } = {}) {
  const auth0 = useAuth0();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: discoveryKeys.prompts(),
    queryFn: () => getDiscoveryPrompts(auth0),
    enabled: options.enabled !== false,
    staleTime: PROMPTS_STALE_MS,
  });

  return {
    prompts: data?.prompts ?? [],
    isLoadingPrompts: isLoading,
    error: error?.message ?? null,
    refetch,
  };
}

// ----- useDiscoveryAgent (composite) -----

export function useDiscoveryAgent(sessionId: string) {
  const chat = useDiscoveryChat(sessionId);
  const messages = useDiscoveryMessages(sessionId);
  const context = useDiscoveryContext(sessionId);
  const insights = useDiscoveryInsights(sessionId);

  return {
    sendMessage: chat.sendMessage,
    isSending: chat.isSending,
    sendError: chat.error,
    resetSend: chat.reset,

    messages: messages.messages,
    isLoadingMessages: messages.isLoading,
    messagesError: messages.error,
    fetchMoreMessages: messages.fetchNextPage,
    hasMoreMessages: messages.hasNextPage,
    isFetchingMoreMessages: messages.isFetchingNextPage,
    refreshMessages: messages.refetch,

    context: context.context,
    isLoadingContext: context.isLoading,
    contextError: context.error,
    refreshContext: context.refetch,

    insights: insights.insights,
    isLoadingInsights: insights.isLoading,
    insightsError: insights.error,
    fetchMoreInsights: insights.fetchNextPage,
    hasMoreInsights: insights.hasNextPage,
    isFetchingMoreInsights: insights.isFetchingNextPage,
    refreshInsights: insights.refetch,
  };
}

// ----- useSessionOnePagers -----

/**
 * Fetch all one-pagers for a session in a single call.
 * Each entry has messageId and optionally artifactId.
 * Used by both the chat message list and the insights panel.
 */
export function useSessionOnePagers(sessionId: string) {
  const auth0 = useAuth0();
  return useQuery<OnePager[]>({
    queryKey: discoveryKeys.sessionOnePagers(sessionId),
    queryFn: () => fetchSessionOnePagers(sessionId, auth0),
    enabled: Boolean(sessionId),
    staleTime: 30_000,
  });
}

// ----- useCreateOnePager -----

/**
 * POST /one-pager — generates title + description via ML and persists to DB in one call.
 * Accepts optional artifactId to link the one-pager to an artifact card.
 * On success, updates the session-level one-pager cache so the View button appears immediately.
 */
export function useCreateOnePager(sessionId: string) {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  return useMutation<OnePager, Error, CreateOnePagerVariables>({
    mutationFn: (vars: CreateOnePagerVariables) => createOnePager(vars, auth0),
    onSuccess: (data) => {
      // Seed/update session cache so UI flips to "View one-pager" immediately
      queryClient.setQueryData<OnePager[]>(
        discoveryKeys.sessionOnePagers(sessionId),
        (prev) => {
          if (!prev) return [data];
          // Replace existing entry for same messageId (+ artifactId if present), or append
          const idx = prev.findIndex(
            (p) =>
              p.messageId === data.messageId &&
              p.artifactId === data.artifactId
          );
          if (idx !== -1) {
            const next = [...prev];
            next[idx] = data;
            return next;
          }
          return [...prev, data];
        }
      );
    },
  });
}
